import { useEffect, useState } from 'react'
import {
  fetchInvoices,
  fetchPaymentBatches,
  fetchAgentCommissions,
  previewExternalInvoice,
  issueExternalInvoice,
  refreshExternalInvoice,
  externalInvoicePdfUrl,
  type Invoice,
  type PaymentBatch,
  type AgentCommission,
} from '../api/finance'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  DataTable,
  EmptyState,
  FilterBar,
  Page,
  PageHeader,
  PaneLayout,
  Select,
  StatCard,
  StatGrid,
  useConfirm,
} from '../components/ui'

/**
 * F-146 (TASK-0400): emissione in fatturazione.equalstudio.it. Prima l'anteprima (dry_run, nulla viene salvato),
 * poi "Emetti" (vale 10 minuti). Il CRM non invia a SdI. Si vede solo con il flag `finanza.emissione_fatturazione`.
 */
function EmissioneFatturazioneCard({ invoices }: { invoices: Invoice[] }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState<number | null>(null)
  const [previewed, setPreviewed] = useState<Record<number, string>>({})
  const [issued, setIssued] = useState<Record<number, { number: string; status: string }>>({})
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null)

  const run = async (id: number, action: () => Promise<void>) => {
    setBusy(id)
    setMessage(null)
    try {
      await action()
    } catch (e: unknown) {
      setMessage({ kind: 'danger', text: e instanceof Error ? e.message : 'Operazione non riuscita' })
    } finally {
      setBusy(null)
    }
  }

  const open = invoices.filter(i => i.status === 'draft' || i.status === 'sent')
  // Lo stato "emessa" viene dal server (sopravvive al ricaricamento); l'esito dell'azione appena fatta lo precede.
  const issuedOf = (i: Invoice): { number: string; status: string } | undefined =>
    issued[i.id] ?? (i.externalDocumentId != null && i.externalStatus !== 'esito_incerto'
      ? { number: i.externalNumber ?? '-', status: i.externalStatus ?? '' }
      : undefined)
  const uncertain = (i: Invoice) => !issued[i.id] && i.externalStatus === 'esito_incerto'
  return (
    <Card variant="outlined">
      <CardHeader title="Emissione in fatturazione" icon="receipt_long" />
      <CardContent>
        {message && <Callout tone={message.kind === 'danger' ? 'error' : 'success'}>{message.text}</Callout>}
        {open.length === 0 ? (
          <div className="text-muted">Nessuna fattura da emettere.</div>
        ) : (
          <DataTable variant="plain">
            <tbody>
              {open.map(i => (
                <tr key={i.id}>
                  <td>{i.invoiceNumber}</td>
                  <td className="text-muted">
                    {issuedOf(i)
                      ? `Emessa ${issuedOf(i)!.number} (${issuedOf(i)!.status})`
                      : uncertain(i)
                      ? 'Esito incerto: il documento potrebbe già esistere in fatturazione. Verificalo prima di riemettere.'
                      : previewed[i.id] ? `Anteprima valida, numero previsto ${previewed[i.id]}` : 'Da emettere'}
                  </td>
                  <td className="text-end">
                    <div className="d-inline-flex flex-wrap gap-1 justify-content-end">
                      <Button
                        size="sm" variant="outlined" disabled={busy === i.id}
                        onClick={() => run(i.id, async () => {
                          const r = await previewExternalInvoice(i.id)
                          setPreviewed(p => ({ ...p, [i.id]: r.expectedNumber ?? '-' }))
                        })}
                      >Anteprima</Button>
                      <Button
                        size="sm" variant="outlined"
                        disabled={busy === i.id || (previewed[i.id] === undefined && !issuedOf(i)) || uncertain(i)}
                        onClick={() => run(i.id, async () => {
                          const r = await issueExternalInvoice(i.id)
                          setIssued(p => ({ ...p, [i.id]: { number: r.number, status: r.status } }))
                          setMessage({ kind: 'success', text: r.replay ? 'Fattura già emessa.' : `Fattura ${r.number} emessa.` })
                        })}
                      >Emetti in fatturazione</Button>
                      {uncertain(i) && (
                        <Button
                          size="sm" variant="outlined"
                          disabled={busy === i.id || previewed[i.id] === undefined}
                          title="Serve un'anteprima valida. Solo dopo aver verificato in fatturazione che il documento non esista."
                          onClick={async () => {
                            if (!(await confirm({
                              title: 'Riemettere la fattura',
                              message: 'Il documento potrebbe già esistere in fatturazione. Riemettere comunque?',
                              confirmLabel: 'Riemetti',
                            }))) return
                            void run(i.id, async () => {
                              const r = await issueExternalInvoice(i.id, true)
                              setIssued(p => ({ ...p, [i.id]: { number: r.number, status: r.status } }))
                              setMessage({ kind: 'success', text: `Fattura ${r.number} emessa.` })
                            })
                          }}
                        >Riemetti (esito incerto)</Button>
                      )}
                      {issuedOf(i) && (
                        <>
                          <Button
                            size="sm" variant="outlined" disabled={busy === i.id}
                            onClick={() => run(i.id, async () => {
                              const r = await refreshExternalInvoice(i.id)
                              setIssued(p => ({ ...p, [i.id]: { number: r.number ?? issuedOf(i)?.number ?? '-', status: r.status } }))
                            })}
                          >Aggiorna stato</Button>
                          {/* Link esterno (PDF): stesse classi di <Button>, ButtonLink è solo per rotte interne. */}
                          <a
                            className="md-button md-button--outlined md-tone--primary md-button--sm md-state md-touch"
                            href={externalInvoicePdfUrl(i.id)} target="_blank" rel="noreferrer"
                          >PDF</a>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </CardContent>
    </Card>
  )
}

export function FinancePage() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const emissioneFatturazione = useFeatureFlag('finanza.emissione_fatturazione')

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [batches, setBatches] = useState<PaymentBatch[]>([])
  const [commissions, setCommissions] = useState<AgentCommission[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      fetchInvoices({ pageSize: 200 }),
      fetchPaymentBatches({ pageSize: 200 }),
      fetchAgentCommissions({ page: 1 }),
    ])
      .then(([invResult, batchResult, commResult]) => {
        setInvoices(invResult.items)
        setBatches(batchResult.items)
        setCommissions(commResult.items)
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
      .finally(() => setLoading(false))
  }, [year])

  // KPI computations.
  // Amount fields (Invoice.total, PaymentBatch.totalAmount/netAmount,
  // AgentCommission.commissionAmount) are omitted entirely by the backend
  // (not null) for a caller without CanSeeRealAmounts (TASK-0087 field-level
  // redaction). A missing value is treated as 0 in these aggregate sums —
  // the resulting KPI is correctly understated for redacted rows, which is
  // expected since the user isn't entitled to see those values anyway.
  const invoiceTotal = invoices.reduce((s, i) => s + (i.total ?? 0), 0)
  const invoiceCount = invoices.length
  const invoiceConfirmed = invoices.filter(i => i.status === 'paid').length

  const confirmedBatches = batches.filter(b => b.status === 'confirmed')
  const pendingBatches = batches.filter(b => b.status === 'pending')
  // NOTE (TASK-0090): these two reduces previously read `b.totalNetAmount`, a
  // field that has never existed on the backend's PaymentBatch response (the
  // real fields are `netAmount`/`totalAmount` — see api/finance.ts). That was
  // a pre-existing bug independent of TASK-0087 (always produced NaN here),
  // fixed as part of this task since it's the same "field access can fail"
  // class of problem in the same file. Using `netAmount` as the closest
  // semantic match to the original (bugged) "totalNetAmount" name.
  const paidBatchTotal = confirmedBatches.reduce((s, b) => s + (b.netAmount ?? 0), 0)
  const pendingBatchTotal = pendingBatches.reduce((s, b) => s + (b.netAmount ?? 0), 0)

  const outstandingInvoices = invoices.filter(i => !['paid', 'cancelled'].includes(i.status))
  const outstandingTotal = outstandingInvoices.reduce((s, i) => s + (i.total ?? 0), 0)
  const overdueCount = outstandingInvoices.filter(
    i => i.dueDate !== null && i.dueDate !== undefined && new Date(i.dueDate) < new Date()
  ).length

  const commissionTotal = commissions.reduce((s, c) => s + (c.commissionAmount ?? 0), 0)
  const commissionPaid = commissions.filter(c => c.status === 'paid').length
  const commissionPending = commissions.filter(c => c.status === 'pending').length

  const years = Array.from({ length: 6 }, (_, i) => currentYear - i)

  return (
    <Page>
      <PageHeader
        title="Panoramica"
        icon="account_balance"
      />

      <FilterBar>
        <Select label="Anno" value={year} onChange={e => setYear(Number(e.target.value))}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </Select>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <div className="text-center"><CircularProgress size="sm" /></div>
      ) : (
        <>
          <StatGrid>
            <StatCard
              label={`Fatturato ${year}`}
              icon="trending_up"
              value={`€ ${invoiceTotal.toLocaleString('it-IT')}`}
              supporting={`${invoiceCount} contratti, ${invoiceConfirmed} confermati`}
            />
            <StatCard
              label="Pagato"
              icon="payments"
              tone="success"
              value={`€ ${paidBatchTotal.toLocaleString('it-IT')}`}
              supporting={`${confirmedBatches.length} batch pagati, ${pendingBatches.length} in attesa`}
            />
            <StatCard
              label="Fatture Scoperte"
              icon="receipt_long"
              tone="warning"
              value={`€ ${outstandingTotal.toLocaleString('it-IT')}`}
              supporting={`${outstandingInvoices.length} fatture, ${overdueCount} scadute`}
            />
            <StatCard
              label="Provvigioni"
              icon="percent"
              value={`€ ${commissionTotal.toLocaleString('it-IT')}`}
              supporting={`${commissionPaid} pagate, ${commissionPending} pendenti`}
            />
          </StatGrid>

          {emissioneFatturazione && <EmissioneFatturazioneCard invoices={invoices} />}

          <Card variant="outlined">
            <CardHeader title={`Fatturato Mensile ${year}`} icon="bar_chart" />
            <CardContent>
              <EmptyState icon="bar_chart" title="Nessun dato per quest'anno" />
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader
              title="Pagamenti batch"
              icon="payments"
              actions={<Button size="sm" variant="tonal">Vedi tutti</Button>}
            />
            <CardContent>
              <StatGrid>
                <StatCard label="Totale batch" value={batches.length.toLocaleString('it-IT')} />
                <StatCard label="Importo confermato" value={`€ ${paidBatchTotal.toLocaleString('it-IT')}`} />
                <StatCard label="In attesa" value={`€ ${pendingBatchTotal.toLocaleString('it-IT')}`} />
              </StatGrid>
            </CardContent>
          </Card>

          <PaneLayout
            variant="supporting"
            main={
              <Card variant="outlined">
                <CardHeader title="Budget" icon="savings" />
                <CardContent>
                  <EmptyState icon="account_balance_wallet" title="Nessun budget configurato" />
                </CardContent>
              </Card>
            }
            pane={
              <Card variant="outlined">
                <CardHeader title="Report finanziari" icon="summarize" />
                <CardContent>
                  <EmptyState icon="description" title="Nessun report generato" />
                </CardContent>
              </Card>
            }
          />
        </>
      )}
    </Page>
  )
}
